<template>
    <div class="fco-app-content" :class="themeClass">
        <div v-if="isLocalTestProfile && showProfile" class="profile-label align-items-center d-flex py-1 px-3 text-bold">
            <div class="flex-fill text-sm">connected to test resources</div>
            <a href="#" @click.prevent="showProfile = false" class="ml-3 text-lg text-white text-bold text-decoration-none">&times;</a>
        </div>
        <template v-if="!isErrorPage && !disableHeader && !isFullScreen">
            <template v-if="isAuthorizedUser && !isLoginPage && !hasUserAccessRequirements">
                <BroadcastMessages />
                <AdminBar class="d-none d-lg-block d-print-none" />
                <MainNav />
                <InfoBar v-show="!isHideInfoBar" />
            </template>
            <PublicHeader v-else />
        </template>

        <div class="flex-fill">
            <template v-if="isSPA">
                <RouterView />
            </template>
            <slot v-else />
        </div>

        <FcoFooter v-if="!isErrorPage && !disableFooter && !isFullScreen" />

        <FcoToast />
        <FcoLoading />
        <MobileAppBanner />
    </div>
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex';
import AdminBar from '@/common/components/AdminBar.vue';
import MainNav from '@/common/components/MainNav.vue';
import InfoBar from '@/common/components/InfoBar.vue';
import FcoFooter from '@/common/components/Footer.vue';
import PublicHeader from '@/common/components/header/PublicHeader.vue';
import BroadcastMessages from '@/common/components/header/BroadcastMessages.vue';
import MobileAppBanner from '@/common/components/MobileAppBanner.vue';
import { addGlobalData, trackUserLogIn } from '@/fcoModules/analytics';
import { initOracleChat } from '@/common/utilities/oracleChat/oracleChatWebsite';
import { BgColorPreference } from '../constants/user';
import loginErrorMixin from '../mixins/loginErrorMixin';
import featureFlagsMixin from '../mixins/featureFlagsMixin';
import { isRequestSettled } from '../store/request-status';
import { StorageKey } from '../store/store-utils';

export default {
    name: 'BaseApp',
    BgColorPreference,
    // TODO: These props are only used when !isSPA. Remove once we are fully on SPA.
    props: {
        showInfoBar: {
            type: Boolean,
            default: true,
        },
        disableHeader: {
            type: Boolean,
            default: false,
        },
        disableFooter: {
            type: Boolean,
            default: false,
        },
    },
    components: {
        BroadcastMessages,
        PublicHeader,
        AdminBar,
        MainNav,
        InfoBar,
        FcoFooter,
        MobileAppBanner,
    },
    mixins: [loginErrorMixin, featureFlagsMixin],
    data() {
        return {
            showProfile: true,
            loadingUserOverlay: null,
        };
    },
    computed: {
        ...mapState([
            'user',
            'masqueradeSource',
            'requests',
            'isSPA',
            'isPunchoutComplete',
            'profile',
            'auth',
            'currentShop',
            'isTermsAcceptRequired',
        ]),
        ...mapGetters(['isMasquerade', 'isAuthorizedUser', 'isAuthExpired', 'isRefreshExpired', 'userDefaultLandingPage']),
        isPasswordChangeRequired() {
            if (!this.isSPA) {
                return this.user?.passwordChangeRequired;
            }
            return this.$store.state.isPasswordChangeRequired;
        },
        isLoadingUser() {
            return !isRequestSettled(this.requests.getUser);
        },
        isLoginPage() {
            if (!this.isSPA) {
                return ['/FirstCallOnline/', '/FirstCallOnline/login.html'].includes(window.location.pathname);
            }
            return this.$route.path === '/login';
        },
        needsLoadingUserOverlay() {
            return !this.isRefreshExpired && !this.user && this.isLoadingUser;
        },
        isErrorPage() {
            return this.$route?.name === 'NotFound' || this.$route?.name?.indexOf('Error') > -1;
        },
        isFullScreen() {
            return this.$route?.matched.some(({ meta }) => meta.isFullScreen);
        },
        isHideInfoBar() {
            return !this.showInfoBar || Boolean(this.$route?.matched?.some(({ meta }) => meta.isHideInfoBar));
        },
        themeClass() {
            const theme = this.user?.userPreference?.selectedStylesheet;
            if (theme === BgColorPreference.GRAY) {
                return 'content-subtle-gray';
            }
            return '';
        },
        isLocalTestProfile() {
            return this.profile === 'local-test' || (this.profile === 'test' && __FCO_IS_LOCAL_DEV_SERVER__);
        },
        /**
         * Represents one of four possible authentication states:
         *  1. Public - unauthenticated user
         *  2. Password change restriction - user is authenticated but access is restricted until password is updated
         *  3. Terms acceptance restriction - user is authenticated but access is restricted until terms are accepted
         *  4. Authenticated
         *
         * This allows us to watch for changes to auth status and respond accordingly (i.e. checking the current route guard)
         */
        authStatus() {
            const isPunchingOut = this.isSPA && this.$route.path === '/punchout' && !this.isPunchoutComplete;
            if (!this.isSPA || (this.auth && !this.isAuthExpired)) {
                if (this.isAuthorizedUser && !isPunchingOut) {
                    return 'AUTHORIZED';
                }
                if (this.isPasswordChangeRequired) {
                    return 'RESTRICTED_PASSWORD';
                }
                if (this.isTermsAcceptRequired) {
                    return 'RESTRICTED_TERMS';
                }
            }
            return 'PUBLIC';
        },
        hasUserAccessRequirements() {
            if (this.isMasquerade) return false;
            if (!this.isSPA) {
                return window.location.href.includes('/legal/terms/accept.html') || Boolean(this.user?.passwordChangeRequired);
            }
            return this.isPasswordChangeRequired || this.isTermsAcceptRequired;
        },
        currentLocale() {
            return this.$store.state.i18n.locale;
        },
    },
    methods: {
        trackLoginSuccess() {
            // check the session storage in Login.vue for a saved login name
            const authAttemptLoginName = sessionStorage.getItem(StorageKey.AUTH_ATTEMPT_LOGIN_NAME);
            if (!authAttemptLoginName) return;

            sessionStorage.removeItem(StorageKey.AUTH_ATTEMPT_LOGIN_NAME);
            trackUserLogIn({
                loginSuccessful: this.isAuthorizedUser,
                userEmail: authAttemptLoginName,
                userId: this.user.userId,
                isSSO: false,
            });
        },
        /**
         * Set the default locale
         * Check for a locale set by the client (in this order):
         *     1. 'language' query parameter
         *     2. 'locale' set in localStorage
         *     3. browser settings
         */
        async setDefaultLocale() {
            const languageQueryParam = this.isSPA ? this.$route.query.language : new URLSearchParams(window.location.search).get('language');
            this.$store.dispatch('i18n/selectDefaultLocale', languageQueryParam);
        },
        async initializeAuth() {
            if (!this.isSPA) {
                await this.$store.dispatch('requestIfIdle', ['getUser']);
                return;
            }

            if (this.isRefreshExpired) {
                this.$store.dispatch('logout');
                return;
            }

            if (this.isAuthExpired) {
                await this.$store.dispatch('refreshAuth');
            } else {
                this.$store.dispatch('startRefreshTimer');
            }

            if (!this.isAuthExpired) {
                this.$store.dispatch('requestIfIdle', ['getUser']);
            }
        },
        isValidRoute(route) {
            return this.$router.match(route).matched.every(({ path }) => path !== '*');
        },
        /**
         * Redirect to a page unless the specified path is the same as the current page.
         * Avoids redundant navigation.
         * @param path string
         */
        redirectTo(path) {
            if (!this.isSPA) {
                const currentPath = window.location.pathname;
                if (!currentPath.includes(path)) {
                    window.location.href = this.fcoUrl(path);
                }
                return;
            }

            if (this.$route.path !== path) {
                this.$router.push(path);
            }
        },
        updateTitle() {
            const titleKey = this.fcoM(this.$route.meta.titleKey);
            document.title = titleKey
                ? `${this.fcoM('pagetitle.default', 'O\'Reilly Pro')} | ${this.fcoM(this.$route.meta.titleKey)}`
                : this.fcoM('pagetitle.default', 'O\'Reilly Pro');
        },
        ...mapActions('usageTracking', ['loadUsageTracking', 'resetUsageTrackingData']),
    },
    watch: {
        isEntSearchEnabled: {
            handler(val) {
                addGlobalData({ enterprise_search: val });
            },
            immediate: true,
        },
        features: {
            handler(val) {
                addGlobalData({ globalFeatures: this.getEnabledFlagNames(val) });
            },
            immediate: true,
        },
        featuresForStore: {
            handler(val) {
                addGlobalData({ featuresForStore: this.getEnabledFlagNames(val) });
            },
            immediate: true,
        },
        needsLoadingUserOverlay: {
            handler(needsLoadingUserOverlay) {
                if (needsLoadingUserOverlay) {
                    this.loadingUserOverlay = this.$fcoLoading();
                } else if (this.loadingUserOverlay) {
                    this.loadingUserOverlay.remove();
                    this.loadingUserOverlay = null;
                }
            },
            immediate: true,
        },
        async authStatus() {
            if (!this.isSPA) {
                const loginPath = '/login.html';
                if (!this.user.authenticated) {
                    // User was logged out in a different tab. Need to redirect to login.
                    this.redirectTo(loginPath);
                } else if (this.isAuthorizedUser) {
                    // User logged in in a different tab but is on the login page. Need to redirect to default landing page.
                    if (window.location.pathname.includes(loginPath)) {
                        this.redirectTo(this.userDefaultLandingPage);
                    }
                } else if (this.isPasswordChangeRequired) {
                    this.redirectTo('/securitychange.html');
                }
                return;
            }

            // If there are no matched routes, we can't validate anything, so just return
            if (!this.$route.matched?.length) return;

            const isAuthorizedRoute = this.$route.matched?.every(({ meta }) => !meta.authorize || meta.authorize());

            if (isAuthorizedRoute) return;

            // Authenticated users without restrictions
            if (this.isAuthorizedUser) {
                // If we have a saved redirect path (user was redirected to login), and it's a valid route, go there
                let redirectPath = sessionStorage.getItem(StorageKey.POST_LOGIN_REDIRECT_PATH);
                sessionStorage.removeItem(StorageKey.POST_LOGIN_REDIRECT_PATH);

                if (!redirectPath || !this.isValidRoute(redirectPath)) {
                    redirectPath = this.userDefaultLandingPage;
                }
                this.redirectTo(redirectPath);
                return;
            }

            if (this.hasUserAccessRequirements) {
                if (this.isPasswordChangeRequired) {
                    this.redirectTo('/security-change');
                } else if (this.isTermsAcceptRequired) {
                    this.redirectTo('/accept-terms');
                }
                return;
            }

            // If we have an auth token but no user or restrictions on the user, it's likely that we're just waiting for the user request to finish
            // This scenario is not likely, but in the event we get here, we'll exit since we don't yet have a valid user for route validation
            if (this.auth) return;

            this.redirectTo('/login');
            this.$fcoLoading.removeAll();
        },
        isMasquerade() {
            if (!this.isSPA) {
                const redirectUrl = this.isMasquerade ? this.fcoUrl(this.userDefaultLandingPage) : this.masqueradeSource; // Wasn't working with the fcoUrl function

                window.location.href = redirectUrl;

                if (!this.isMasquerade) {
                    this.$store.commit('setMasqueradeSource', null);
                }

                return;
            }

            this.resetUsageTrackingData();

            if (this.isMasquerade) {
                // User has started masquerading
                // Always redirect to the default landing page after starting to masquerade
                this.$router.push(this.userDefaultLandingPage);
                return;
            }

            // When removing the masquerade, try to redirect back to the page the user was on before they started masquerading
            // Make sure the saved value is a valid route. If not, default to the home page.
            const isValidMasqueradeSourceRoute = Boolean(this.masqueradeSource) && this.isValidRoute(this.masqueradeSource);
            const unMasqueradeRedirect = isValidMasqueradeSourceRoute ? this.masqueradeSource : '/';
            this.$store.commit('setMasqueradeSource', null);
            if (!this.masqueradeSource !== this.$route.fullPath) {
                this.$router.push(unMasqueradeRedirect);
            }
        },
        currentLocale() {
            if (this.isSPA) {
                this.updateTitle();
            }
        },
    },
    async created() {
        this.setDefaultLocale();
        await this.initializeAuth();
        this.$store.dispatch('requestIfIdle', ['getAppData']);
        if (this.isAuthorizedUser) {
            await this.$store.dispatch('requestIfIdle', ['getIsEarnbackEligible', 'getCurrentShop']);
            this.loadUsageTracking();

            // add accountId to data layer for GTM
            addGlobalData({ accountId: this.currentShop.accountNumber });
        }

        if (this.loginErrorMessage && !this.isLoginPage) {
            this.$fcoToast.error(this.loginErrorMessage);
        }

        initOracleChat();
        this.trackLoginSuccess();
    },
};
</script>

<style scoped lang="scss">
.fco-app-content {
    min-height: 100vh;
    display: flex;
    flex-direction: column;
}
.profile-label {
    position: fixed;
    top: 0;
    left: 50%;
    transform: translateX(-50%);
    color: white;
    background-color: blue;
    border: black 1px solid;
    text-transform: uppercase;
    z-index: 100;
}
</style>
